
import { computed, defineComponent, onMounted, ref } from "vue";
import FundingRoundStageUpdate from "@/components/wizard/steps/FundingRoundStageUpdate.vue";
import FundingRequirementsUpdate from "@/components/wizard/steps/FundingRequirementsUpdate.vue";
import RoundTimelineUpdate from "@/components/wizard/steps/RoundTimelineUpdate.vue";
import { StepperComponent } from "@/assets/ts/components";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";

interface IStep1 {
  name: string;
  jobTitle: string;
  telephone: string;
  linkedInProfileUrl: string;
}

interface IStep2 {
  introduction: string;
  bio: string;
}

interface IStep3 {
  linkToExternalDocuments: string;
  documents: unknown;
}

interface IStep4 {
  expertIndustrySectors: Array<number | string>;
  expertExpertise: Array<number | string>;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4 {}

export default defineComponent({
  name: "update-funding-round",
  components: {
    FundingRoundStageUpdate,
    FundingRequirementsUpdate,
    RoundTimelineUpdate,
  },
  setup() {
    const loading = ref<boolean>(false);
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const fundingRoundKeyEnums = ref<Record<string, unknown>>({
      businessId: "businessId",
      fundingRoundClosesAt: "fundingRoundClosesAt",
      founderBusinessId: "founderBusinessId",
      id: "id",
      fundingRoundInvestmentRequired: "fundingRoundInvestmentRequired",
      fundingRoundMinimumInvestment: "fundingRoundMinimumInvestment",
      fundingRoundName: "fundingRoundName",
      fundingRoundOpensAt: "fundingRoundOpensAt",
      fundingRoundPreMoneyValuation: "fundingRoundPreMoneyValuation",
    });
    const businesses = computed(() => store.getters.getBusinesses);
    const business = ref<Record<string, unknown>>({});
    const fundingRound = ref<Record<string, unknown>>({});
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const formData = ref<CreateAccount>({
      name: "",
      jobTitle: "",
      telephone: "",
      linkedInProfileUrl: "",
      introduction: "",
      bio: "",
      linkToExternalDocuments: "",
      documents: null,
      expertIndustrySectors: [],
      expertExpertise: [],
    });

    onMounted(async () => {
      if (!businesses.value?.founderBusinesses?.length) {
        try {
          const response = await store.dispatch(
            Actions.GET_FOUNDER_BUSINESSES,
            {
              page: 1,
              perPage: 10,
            }
          );
          if (response !== true) throw new Error();
          business.value = businesses.value?.founderBusinesses.find(
            (b) => b.id == route.query.businessId
          );
        } catch (err) {
          console.log(err);
        }
      } else
        business.value = businesses.value?.founderBusinesses.find(
          (b) => b.id == route.query.businessId
        );
      fundingRound.value =
        ((
          business.value?.fundingRounds as Array<Record<string, unknown>>
        )?.find((round) => round.id == route.params.id) as Record<
          string,
          unknown
        >) ?? {};
      for (let key in fundingRoundKeyEnums.value) {
        if (key !== fundingRoundKeyEnums.value[key]) {
          console.log(fundingRound.value, fundingRoundKeyEnums.value[key], Object.getOwnPropertyDescriptor(
              fundingRound.value,
              key
            ), key)
          Object.defineProperty(
            fundingRound.value,
            fundingRoundKeyEnums.value[key] as symbol,
            Object.getOwnPropertyDescriptor(
              fundingRound.value,
              key
            ) as PropertyDescriptor
          );
          delete fundingRound.value[key];
        }
      }
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );

      setCurrentPageBreadcrumbs("Update Funding Round", [
        "Businesses",
        "Business Details",
      ]);
    });
    const createAccountSchema = [
      Yup.object({
        fundingRoundName: Yup.string().required().label("Name"),
      }),
      Yup.object({
        fundingRoundName: Yup.string().required().label("Name"),
        fundingRoundInvestmentRequired: Yup.string()
          .required()
          .label("Investment Required"),
        fundingRoundPreMoneyValuation: Yup.string()
          .required()
          .label("Pre-Money Valuation"),
        fundingRoundMinimumInvestment: Yup.string()
          .required()
          .label("Minimum Investment"),
      }),
      {},
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value];
    });

    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit((values) => {
      console.log({ values });
      formData.value = {
        ...formData.value,
        ...values,
      };

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const formSubmit = async () => {
      loading.value = true;
      try {
        fundingRound.value["founderBusinessId"] = business.value.id;
        const response = await store.dispatch(Actions.UPDATE_FUNDING_ROUND, {
          id: route.params.id,
          data: fundingRound.value,
        });
        if (response !== true) throw new Error();
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Funding Round ${"Updated"}`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        setTimeout(() => {
          router.push(`/business/${business.value.id}`);
        }, 2000);
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: `Funding Round Updation Unsuccessful`,
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };

    return {
      loading,
      fundingRound,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
    };
  },
});
